import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { NotificationDto, NotificationId, NotificationType } from '@workspace/data-types';
import { NotificationStateService } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

@Component({
  selector: 'core-notifications-page',
  imports: [MatIcon, MatButton, MatIconButton],
  templateUrl: './notifications-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPageCoreComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  readonly state = inject(NotificationStateService);

  notifications = signal<NotificationDto[]>([]);
  filter = signal<'all' | 'unread'>('all');
  loading = signal(false);
  hasMore = signal(true);
  currentPage = 0;
  limit = 20;

  ngOnInit(): void {
    this.loadNextPage();
  }

  setFilter(newFilter: 'all' | 'unread'): void {
    if (this.filter() === newFilter) return;
    this.filter.set(newFilter);
    this.notifications.set([]);
    this.currentPage = 0;
    this.hasMore.set(true);
    this.loadNextPage();
  }

  loadNextPage(): void {
    if (this.loading()) return;
    this.loading.set(true);

    const unreadOnly = this.filter() === 'unread';

    this.api
      .notifications()
      .getNotifications(this.limit, this.currentPage, unreadOnly)
      .pipe(
        tap((items) => {
          if (items.length < this.limit) {
            this.hasMore.set(false);
          }
          this.notifications.update((list) => [...list, ...items]);
          this.currentPage++;
        }),
        catchError(() => EMPTY),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  goBack(): void {
    void this.router.navigate(['/']);
  }

  markAsRead(id: NotificationId): void {
    this.state.markAsRead(id);
    this.notifications.update((list) => list.map((n) => (n._id === id ? { ...n, read: true } : n)));
  }

  markAllAsRead(): void {
    this.state.markAllAsRead();
    this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
  }

  getIconName(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'circle-check';
      case NotificationType.WARNING:
        return 'triangle-alert';
      case NotificationType.ERROR:
        return 'circle-x';
      case NotificationType.SYSTEM:
        return 'settings';
      case NotificationType.INFO:
      default:
        return 'info';
    }
  }

  getBorderColorClass(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'border-l-green-9';
      case NotificationType.WARNING:
        return 'border-l-amber-9';
      case NotificationType.ERROR:
        return 'border-l-red-9';
      case NotificationType.SYSTEM:
        return 'border-l-purple-9';
      case NotificationType.INFO:
      default:
        return 'border-l-blue-9';
    }
  }

  getIconColorClass(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'bg-green-2 text-green-9';
      case NotificationType.WARNING:
        return 'bg-amber-2 text-amber-9';
      case NotificationType.ERROR:
        return 'bg-red-2 text-red-9';
      case NotificationType.SYSTEM:
        return 'bg-purple-2 text-purple-9';
      case NotificationType.INFO:
      default:
        return 'bg-blue-2 text-blue-9';
    }
  }

  formatTime(date: Date | string): string {
    if (!date) return '';
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Il y a ${diffDays} j`;
  }
}
